import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { extractUrlParams, EncodedDataParams, ENCODED_DATA_PARAM, STORAGE_KEY_MAPPINGS } from '../utils/sessionUtils';

const TestParamsPage: React.FC = () => {
  const [params, setParams] = useState<EncodedDataParams>({});
  const [testUrl, setTestUrl] = useState<string>('');
  const [jsonString, setJsonString] = useState<string>('');
  
  useEffect(() => {
    // Extract parameters from URL
    const extractedParams = extractUrlParams();
    setParams(extractedParams);
    
    // Create a test URL with a single encoded JSON parameter
    const createTestUrl = () => {
      // Sample JSON object
      const jsonData = {
        AgencyContactPerson: 'John Doe',
        AgentName: 'Central Clinic',
        AgencyEmail: 'user@example.com'
      };
      
      // Convert to JSON string and encode as base64
      const jsonStr = JSON.stringify(jsonData);
      setJsonString(jsonStr);
      
      // Encode the JSON string (handle UTF-8 characters properly)
      const encodedJson = btoa(encodeURIComponent(jsonStr));
      
      // Create the full URL with a single parameter
      const fullUrl = `${window.location.origin}?${ENCODED_DATA_PARAM}=${encodedJson}`;
      
      setTestUrl(fullUrl);
    };
    
    createTestUrl();
  }, []);
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">URL Parameter Test Page</h1>
      
      <div className="bg-gray-100 p-4 rounded-lg mb-6">
        <h2 className="text-lg font-semibold mb-2">Current URL Parameters:</h2>
        {Object.keys(params).length === 0 ? (
          <p>No parameters found in the URL</p>
        ) : (
          <ul className="list-disc pl-6">
            {Object.entries(params).map(([key, value]) => (
              <li key={key}><strong>{key}:</strong> {value}</li>
            ))}
          </ul>
        )}
      </div>
      
      <div className="bg-blue-50 p-4 rounded-lg mb-6">
        <h2 className="text-lg font-semibold mb-2">Test URL with Encoded JSON Parameter:</h2>
        <div className="bg-white p-3 rounded border break-all">
          <a href={testUrl} className="text-blue-600 hover:underline">
            {testUrl}
          </a>
        </div>
        <p className="mt-2 text-sm text-gray-700">
          Click the link above to test with a single encoded JSON parameter. 
          This will redirect you to the RecOrb page with pre-filled form fields.
        </p>
      </div>
      
      <div className="bg-yellow-50 p-4 rounded-lg mb-6">
        <h2 className="text-lg font-semibold mb-2">JSON Data Reference:</h2>
        <div className="bg-white p-3 rounded border font-mono text-sm overflow-auto">
          {jsonString}
        </div>
        <h3 className="text-md font-semibold mt-4 mb-2">Storage Key Mappings:</h3>
        <ul className="list-disc pl-6">
          {Object.entries(STORAGE_KEY_MAPPINGS).map(([jsonKey, storageKey]) => (
            <li key={jsonKey}>
              <strong>JSON Key:</strong> {jsonKey}<br />
              <strong>Storage Key:</strong> {storageKey}
            </li>
          ))}
        </ul>
      </div>
      
      <div className="flex gap-4">
        <Link 
          to="/" 
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
        >
          Back to Home
        </Link>
        <Link 
          to="/app/recorb" 
          className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 rounded transition-colors"
        >
          Go to RecOrb Form
        </Link>
      </div>
    </div>
  );
};

export default TestParamsPage;
